import { Task } from "@/types/v2";
import { firestoreToDateTime } from "@/lib/utils";

const dueDateMillis = (task: Task): number => {
  const date = firestoreToDateTime(task.dueDate);
  return date ? date.getTime() : Number.POSITIVE_INFINITY;
};

const isVisible = (task: Task): boolean => task._deleted !== true;

export const prepareTasks = (tasks: Task[]): Task[] => {
  return tasks
    .filter(isVisible)
    .slice()
    .sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      return dueDateMillis(a) - dueDateMillis(b);
    });
};

export const countOpenTasks = (tasks: Task[]): number =>
  tasks.filter((task) => !task.completed).length;

// Expects already-prepared tasks (see prepareTasks — soft-deleted stripped),
// exactly like countOpenTasks. The store only ever feeds it prepared input.
export const countMyOpenTasks = (
  tasks: Task[],
  employeeId: string | null | undefined
): number =>
  employeeId
    ? tasks.filter((task) => !task.completed && task.assigneeId === employeeId)
        .length
    : 0;

export const tasksByAssignee = (tasks: Task[], assigneeId: string): Task[] =>
  tasks.filter((task) => task.assigneeId === assigneeId);

export const tasksForCard = (tasks: Task[], cardId: string): Task[] =>
  tasks.filter((task) => task.relatedRO === cardId);

export const taskCounts = (
  tasks: Task[]
): { completed: number; total: number } => ({
  completed: tasks.filter((task) => task.completed).length,
  total: tasks.length,
});

export const TaskStatus = {
  OPEN: "open",
  OVERDUE: "overdue",
  COMPLETED: "completed",
} as const;
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

/** Sentinel value used in assignee filters to mean "no assignee". */
export const UNASSIGNED_FILTER_VALUE = "__unassigned__";

export const isOverdue = (task: Task, now: Date = new Date()): boolean => {
  if (task.completed) return false;
  const due = firestoreToDateTime(task.dueDate);
  return !!due && due.getTime() < now.getTime();
};

export const taskStatus = (task: Task, now: Date = new Date()): TaskStatus => {
  if (task.completed) return TaskStatus.COMPLETED;
  return isOverdue(task, now) ? TaskStatus.OVERDUE : TaskStatus.OPEN;
};

export type TaskListFilters = {
  statuses: TaskStatus[];
  assigneeIds: string[];
  showCompleted: boolean;
};

export const filterTasks = (
  tasks: Task[],
  filters: TaskListFilters,
  now: Date = new Date(),
  // Tasks completed in the current session stay visible despite the
  // hidden-completed default; an explicit status filter still wins.
  stickyCompletedIds?: ReadonlySet<string>
): Task[] =>
  tasks.filter((task) => {
    if (filters.statuses.length > 0) {
      if (!filters.statuses.includes(taskStatus(task, now))) return false;
    } else if (
      task.completed &&
      !filters.showCompleted &&
      !(task.id && stickyCompletedIds?.has(task.id))
    ) {
      return false;
    }
    if (filters.assigneeIds.length > 0) {
      const assigneeKey = task.assigneeId ?? UNASSIGNED_FILTER_VALUE;
      if (!filters.assigneeIds.includes(assigneeKey)) return false;
    }
    return true;
  });

export const canModifyTask = (
  task: Task,
  employeeId: string | undefined,
  isAdmin: boolean
): boolean => {
  if (!task.assigneeId) return true;
  if (employeeId && task.assigneeId === employeeId) return true;
  return isAdmin;
};

export const selectMyOverdueTasks = (
  tasks: Task[],
  employeeId: string | undefined,
  now: Date = new Date()
): Task[] => {
  if (!employeeId) return [];
  return tasks.filter(
    (task) => task.assigneeId === employeeId && isOverdue(task, now)
  );
};

export type OverdueToastModel = {
  count: number;
  task: Task | null;
};

export const buildOverdueToastModel = (
  myOverdue: Task[]
): OverdueToastModel => ({
  count: myOverdue.length,
  task: myOverdue.length === 1 ? myOverdue[0] : null,
});

export type OverdueToastPlan =
  | { type: "none" }
  | { type: "dismiss" }
  | { type: "render"; model: OverdueToastModel; notifyIds: string[] };

/**
 * Session-dedup identity for the overdue toast. Includes the due date so that
 * rescheduling a task (changing its due date) yields a new key and re-arms the
 * notification the next time the task goes overdue — a reschedule is a
 * meaningful state change, not the same alert repeated. Overdue tasks always
 * have a due date; the "none" fallback only occurs for synthetic inputs.
 */
export const overdueNotifyKey = (task: Task): string => {
  const due = firestoreToDateTime(task.dueDate);
  return `${task.id}:${due ? due.getTime() : "none"}`;
};

/**
 * Decides what the overdue-task toast should do given the current overdue set,
 * which notify keys have already been toasted this session, and the count
 * currently shown (null when no toast is showing).
 *
 * - New un-notified key present -> render (first show or a fresh crossing).
 * - Showing but the set size changed -> render (refresh the digest/detail count).
 * - Nothing overdue while showing -> dismiss.
 * - Otherwise -> none.
 */
export const planOverdueToast = (
  myOverdue: Task[],
  notifiedIds: ReadonlySet<string>,
  shownCount: number | null
): OverdueToastPlan => {
  // Filter to id-bearing tasks once, and derive both the notify keys and the
  // render model from the same filtered array, so `model.count` (used to seed
  // `shownCount` on the next call) always matches the number of keys.
  // Otherwise a task with a falsy id would make model.count and the key count
  // diverge, pinning `countChanged` permanently true and re-rendering forever.
  const withId = myOverdue.filter((task): task is Task & { id: string } =>
    Boolean(task.id)
  );
  // Dedup identity is (id + due date), not the raw id — see overdueNotifyKey.
  // `notifiedIds` and the returned `notifyIds` are these keys.
  const notifyIds = withId.map((task) => overdueNotifyKey(task));

  if (withId.length === 0) {
    return shownCount === null ? { type: "none" } : { type: "dismiss" };
  }

  const hasNew = notifyIds.some((key) => !notifiedIds.has(key));
  const countChanged = shownCount !== null && shownCount !== withId.length;

  if (hasNew || countChanged) {
    return {
      type: "render",
      model: buildOverdueToastModel(withId),
      notifyIds,
    };
  }

  return { type: "none" };
};
